import React, { useState, useMemo } from 'react';
import { Banco } from '../../types/financeiro';
import { financeiroService } from '../../services/financeiroService';
import {
  Landmark,
  Building2,
  CheckCircle2,
  Plus,
  Pencil,
  Trash2,
  Search,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { sanitizeText } from '../../utils/textSanitizer';
import { BancoFormModal } from './BancoFormModal';

interface FinanceiroBancosSectionProps {
  bancos: Banco[];
  loading: boolean;
  onRefresh?: () => void;
}

export function FinanceiroBancosSection({ bancos, loading, onRefresh }: FinanceiroBancosSectionProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBanco, setSelectedBanco] = useState<Banco | null>(null);

  // Confirmação de exclusão
  const [bancoToDelete, setBancoToDelete] = useState<Banco | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const filteredBancos = useMemo(() => {
    if (!searchTerm.trim()) return bancos;
    const term = searchTerm.toLowerCase();
    return bancos.filter(
      (b) =>
        String(b.id).includes(term) ||
        (b.nome && sanitizeText(b.nome).toLowerCase().includes(term))
    );
  }, [bancos, searchTerm]);

  const handleOpenCreate = () => {
    setSelectedBanco(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (banco: Banco) => {
    setSelectedBanco(banco);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!bancoToDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const res = await financeiroService.deleteBanco(bancoToDelete.id);
      if (!res.success) {
        setDeleteError(res.error || 'Erro ao excluir banco.');
        setIsDeleting(false);
        return;
      }
      setBancoToDelete(null);
      if (onRefresh) onRefresh();
    } catch (err: any) {
      console.error('Erro ao excluir banco:', err);
      setDeleteError(err.message || 'Erro inesperado ao excluir.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header com Ações */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Instituições Bancárias Cadastradas</h3>
            <p className="text-xs text-slate-500">
              Bancos disponíveis para emissão de cobranças, transferências e baixas financeiras.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-xl">
            {bancos.length} Banco(s)
          </span>
          <button
            type="button"
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-xs hover:shadow-md transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Banco</span>
          </button>
        </div>
      </div>

      {/* Barra de Busca */}
      <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-2xs">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por código ou nome do banco..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:bg-white text-slate-800 placeholder-slate-400"
          />
        </div>
      </div>

      {/* Tabela de Bancos */}
      <div className="bg-white shadow-xs rounded-2xl overflow-hidden border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left font-bold text-slate-600 uppercase tracking-wider w-28">Código / ID</th>
                <th className="px-5 py-3 text-left font-bold text-slate-600 uppercase tracking-wider">Instituição Financeira</th>
                <th className="px-5 py-3 text-left font-bold text-slate-600 uppercase tracking-wider w-36">Status</th>
                <th className="px-5 py-3 text-right font-bold text-slate-600 uppercase tracking-wider w-28">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span>Carregando bancos...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredBancos.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    {searchTerm ? 'Nenhum banco encontrado para a busca.' : 'Nenhum banco cadastrado no sistema.'}
                  </td>
                </tr>
              ) : (
                filteredBancos.map((b) => (
                  <tr key={`banco-${b.id}`} className="hover:bg-slate-50/70 transition-colors group">
                    <td className="px-5 py-3.5 font-bold text-slate-900">#{b.id}</td>
                    <td className="px-5 py-3.5 font-semibold text-slate-800">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                        <span>{sanitizeText(b.nome) || 'Banco sem nome'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        Disponível
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(b)}
                          title="Editar Instituição Bancária"
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError(null);
                            setBancoToDelete(b);
                          }}
                          title="Excluir Banco"
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Cadastro / Edição */}
      <BancoFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        banco={selectedBanco}
        onSuccess={() => {
          if (onRefresh) onRefresh();
        }}
      />

      {/* Modal de Confirmação de Exclusão */}
      {bancoToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-slate-100 p-6 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-amber-600 mb-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-900 text-base">Excluir Banco?</h3>
            </div>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Tem certeza que deseja remover o banco <b>"{sanitizeText(bancoToDelete.nome)}"</b> (#{bancoToDelete.id})?
            </p>
            {deleteError && (
              <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs">
                {deleteError}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setBancoToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all disabled:opacity-50 cursor-pointer"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Sim, Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
